import React, { useMemo } from "react";
import { TableRow, TableCell, Tooltip, IconButton, Box, Typography } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "@fluentui/react/lib/Icon";

import { IniInfo } from "system/types/interfaces";
import { DeleteRegular, EditRegular } from "@fluentui/react-icons";
import { getConfigLineError } from "components/modules/policy-editor/utils/configValueQuoting";

interface SortableConfigurationRowProps {
  item: IniInfo;
  index: number;
  onAdd: (index: number) => void;
  onDelete: (ruleId: string) => void;
  onUndoDelete: (ruleId: string) => void;
  onEdit: (rule: IniInfo) => void;
  onToggleComment?: (id: string, isComment: boolean) => void;
  readOnlyViewer: boolean;
}

export const SortableConfigurationRow: React.FC<SortableConfigurationRowProps> = ({
  item,
  index,
  onAdd,
  onDelete,
  onUndoDelete,
  onEdit,
  onToggleComment,
  readOnlyViewer,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id! });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isDeleted = item.isDeleted || false;

  // Rows loaded before isComment was carried through still only have the leading semicolon.
  const isCommentRow =
    item.isComment ?? (Boolean(item.value?.trim().startsWith(";")) || Boolean(item.name?.trim().startsWith(";")));

  const uncommentError = useMemo(
    () => (isCommentRow ? getConfigLineError(item.value ?? "") : null),
    [isCommentRow, item.value]
  );
  const canUncomment = uncommentError === null;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      sx={{
        backgroundColor: isDeleted ? "#2a2a2a" : "inherit",
        "&:hover": {
          backgroundColor: isDeleted ? "#2a2a2a" : "#333",
        },
      }}
    >
      {/* Drag Cell */}
      <TableCell
        {...listeners}
        {...attributes}
        sx={{
          cursor: "grab",
          width: "60px",
          opacity: isCommentRow ? 0.6 : 1,
        }}
      >
        <Tooltip title="Drag to reorder" arrow>
          <span><Icon iconName="GripperBarHorizontal" style={{ color: isCommentRow ? "#4caf50" : "#757575" }} /></span>
        </Tooltip>
      </TableCell>

      {isCommentRow ? (
        /* A commented row has no structure to show, so it takes the whole content width */
        <TableCell
          colSpan={3}
          sx={{
            color: "#4caf50",
            fontStyle: "italic",
            textDecoration: isDeleted ? "line-through" : "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {item.value}
        </TableCell>
      ) : (
        <>
          {/* Name Cell */}
          <TableCell
            sx={{
              color: isDeleted ? "#d5878b" : "#fff",
              textDecoration: isDeleted ? "line-through" : "none",
            }}
          >
            {item.name}
          </TableCell>

          {/* Value Cell */}
          <TableCell
            sx={{
              color: isDeleted ? "#d5878b" : "#fff",
              textDecoration: isDeleted ? "line-through" : "none",
            }}
          >
            {item.value}
          </TableCell>

          {/* Applicability Cell */}
          <TableCell
            sx={{
              color: isDeleted ? "#d5878b" : "#fff",
              textDecoration: isDeleted ? "line-through" : "none",
            }}
          >
            {item.delimiter} {item.applicability}
          </TableCell>
        </>
      )}

      {/* Actions Cell */}
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          {item.pendingChanges && (
            <Typography
              variant="body2"
              sx={{
                backgroundColor: isDeleted ? "#d5878b" : "#fbc02d",
                color: "#000",
                padding: "3px 6px",
                borderRadius: "4px",
                fontWeight: "bold",
              }}
            >
              {isDeleted ? "Deleted Row" : "Pending Changes"}
            </Typography>
          )}

          {onToggleComment && !isDeleted && (
            <Tooltip
              title={
                isCommentRow
                  ? canUncomment
                    ? "Uncomment Configuration"
                    : `Cannot uncomment: ${uncommentError}`
                  : "Comment Configuration"
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => onToggleComment(item.id!, isCommentRow)}
                  hidden={readOnlyViewer}
                  disabled={isCommentRow && !canUncomment}
                  sx={{
                    color: isCommentRow ? (canUncomment ? "#4caf50" : "#8a8886") : "#8a8886",
                    "&:hover": { color: isCommentRow ? (canUncomment ? "#66bb6a" : "#8a8886") : "#bdbdbd" },
                  }}
                >
                  <Icon iconName={isCommentRow ? "CommentAdd" : "Comment"} style={{ fontSize: "small" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <Tooltip title="Add line">
            <IconButton
              size="small"
              color="primary"
              onClick={() => onAdd(index)}
              hidden={readOnlyViewer}
            >
              <Icon iconName="Add" />
            </IconButton>
          </Tooltip>

          {isDeleted ? (
            <Tooltip title="Undo Delete" arrow>
              <IconButton size="small" onClick={() => onUndoDelete(item.id!)}>
                <Icon iconName="Undo" />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Delete" arrow>
                <IconButton
                  size="small"
                  color="error"
                  sx={{ color: "#d54c52" }}
                  onClick={() => onDelete(item.id!)}
                  hidden={readOnlyViewer}
                >
                  <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit Configuration" arrow>
                <IconButton
                  size="small"
                  sx={{ color: "#8a8886" }}
                  onClick={() => onEdit(item)}
                  hidden={readOnlyViewer}
                >
                  <EditRegular fontSize={22} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </TableCell>
    </TableRow> 
  );
};
